import React, { useMemo, useRef, useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { B2Button } from "@hdb/biome-2";
import { FormControl } from "react-bootstrap";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import ModalComponent from "src/core/components/modal/modal.component";
import {
  getAnalyticDaylightData,
  getAnalyticLightOPStatusData,
  getAnalyticMotionData,
} from "src/core/api/services/analytics.service";
import AnalyticsChartComponent from "src/modules/dashboard/components/dashboard-floor-plan/dashboard-floor-plan-analytics/components/charts/analytics-chart.component";
import { faChartBar } from "@fortawesome/free-solid-svg-icons";
import "../../../asset.scss";

interface AssetAnalyticsModalProps {
  showModal: boolean;
  handleClose: () => void;
  selectedAssets: string[];
  analyticsDate: string;
  onDateChange: (date: string) => void;
  onCompare: () => void;
}

type AnalyticsTab = "LightOPStatus" | "Daylight" | "Motion";

interface AnalyticsChartData {
  assetId: string;
  name: string;
  data: (number | null)[];
  lightIntensity?: (number | null)[];
}

const AssetAnalyticsModal: React.FC<AssetAnalyticsModalProps> = ({
  showModal,
  handleClose,
  selectedAssets,
  analyticsDate,
  onDateChange,
  onCompare,
}) => {
  const [activeTab, setActiveTab] = useState<AnalyticsTab>("LightOPStatus");
  const [pendingDate, setPendingDate] = useState(analyticsDate);
  const [fetchTrigger, setFetchTrigger] = useState(0);
  const [render, setRender] = useState(0);
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setPendingDate(analyticsDate);
  }, [analyticsDate]);

  useEffect(() => {
    setRender((currentRender) => currentRender + 1);
  }, [activeTab]);

  const lightQuery = useQuery({
    queryKey: ["getAnalyticLightOPStatusData", selectedAssets, analyticsDate, fetchTrigger],
    queryFn: ({ signal }) =>
      getAnalyticLightOPStatusData(
        { assetCodes: selectedAssets, date: analyticsDate, chartType: 1 },
        signal,
      ),
    enabled: showModal && selectedAssets?.length > 0,
  });

  const daylightQuery = useQuery({
    queryKey: ["getAnalyticDaylightData", selectedAssets, analyticsDate, fetchTrigger],
    queryFn: ({ signal }) =>
      getAnalyticDaylightData(
        { assetCodes: selectedAssets, date: analyticsDate, chartType: 1 },
        signal,
      ),
    enabled: showModal && selectedAssets?.length > 0 && activeTab === "Daylight",
  });

  const motionQuery = useQuery({
    queryKey: ["getAnalyticMotionData", selectedAssets, analyticsDate, fetchTrigger],
    queryFn: ({ signal }) =>
      getAnalyticMotionData(
        { assetCodes: selectedAssets, date: analyticsDate, chartType: 1 },
        signal,
      ),
    enabled: showModal && selectedAssets?.length > 0 && activeTab === "Motion",
  });

  const selectedAssetOrder = useMemo(() => {
    return selectedAssets.reduce<Record<string, number>>(
      (order, assetCode, index) => {
        order[assetCode] = index + 1;
        return order;
      },
      {},
    );
  }, [selectedAssets]);

  const motionAnalyticsData = useMemo<AnalyticsChartData[]>(() => {
    if (!motionQuery.data?.data?.length) return [];

    return motionQuery.data.data.map((item: any) => ({
      assetId: item.assetId,
      name: selectedAssetOrder[item.assetId]
        ? `${selectedAssetOrder[item.assetId]} - ${item.displayname}`
        : item.displayname,
      data: item.motiondata,
    }));
  }, [motionQuery.data, selectedAssetOrder]);

  const daylightAnalyticsData = useMemo<AnalyticsChartData[]>(() => {
    if (!daylightQuery.data?.data?.length) return [];

    return daylightQuery.data.data.map((item: any) => ({
      assetId: item.assetId,
      name: selectedAssetOrder[item.assetId]
        ? `${selectedAssetOrder[item.assetId]} - ${item.displayname}`
        : item.displayname,
      data: item.daylightdata,
    }));
  }, [daylightQuery.data, selectedAssetOrder]);

  const lightOPStatusAnalyticsData = useMemo<AnalyticsChartData[]>(() => {
    if (!lightQuery.data?.data?.length) return [];

    return lightQuery.data.data.map((item: any) => ({
      assetId: item.assetId,
      name: selectedAssetOrder[item.assetId]
        ? `${selectedAssetOrder[item.assetId]} - ${item.displayname}`
        : item.displayname,
      data: item.lightOPStatus,
      lightIntensity: item.lightIntensity,
    }));
  }, [lightQuery.data, selectedAssetOrder]);

  const headerIcon = (
    <div className="analytics-header-icon">
      <FontAwesomeIcon icon={faChartBar} />
    </div>
  );

  return (
    <ModalComponent
      showModal={showModal}
      handleClose={handleClose}
      headerTitle="Analytics"
      headerIcon={headerIcon}
      isViewMode={true}
      size="xl"
    >
      {/* <hr /> */}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div />
        <div className="d-flex gap-2">
          <FormControl
            type="date"
            value={pendingDate}
            onChange={(e: any) => setPendingDate(e.target.value)}
            max={moment().format("YYYY-MM-DD")}
            className="analytics-date-input"
          />
          <B2Button
            variant="primary"
            onClick={() => {
              onCompare();
              onDateChange(pendingDate);
              setFetchTrigger((currentTrigger) => currentTrigger + 1);
            }}
          >
            Compare
          </B2Button>
        </div>
      </div>

      {/* <hr /> */}

      <AnalyticsChartComponent
        showChart={showModal}
        showTitle={false}
        selectedAssetCodes={selectedAssets}
        motionData={motionAnalyticsData}
        lightOPStatusData={lightOPStatusAnalyticsData}
        daylightData={daylightAnalyticsData}
        isLoadingMotion={motionQuery.isLoading}
        isLoadingDaylight={daylightQuery.isLoading}
        isLoadingLightOPStatus={lightQuery.isLoading}
        tooltipOutside={false}
        activeKey={activeTab}
        onTabChange={(key) => setActiveTab(key as AnalyticsTab)}
        renderKey={render}
        chartRef={chartRef}
        selectedDate={analyticsDate}
      />
    </ModalComponent>
  );
};

export default AssetAnalyticsModal;
